import {
  getAccessToken,
  performTokenRefresh,
  buildWsUrl,
  resolveApiUrl,
  wsAuthProtocols,
} from '@/shared/api/apiClient';
import {
  normalizeStatus,
  wsStatusPath,
  LIBRARY_BACKEND,
  mapJobProgress,
  type JobProgressApi,
} from '@/shared/api/backendContract';
import type { JobStatusResult } from '@/features/content/services/videoApi';
import type { JobError } from '@/shared/types';

// Mirror of the backend's JobStatusEvent Pydantic model (realtime/events.py).
type WsJobStatusEvent = {
  task_id: string;
  status: string;
  detail?: string | null;
  download_url?: string | null;
  thumbnail_url?: string | null;
  title?: string | null;
  // Optional structured failure detail — the REST path always carries this on a
  // failed job; the realtime event may not, so it's optional/future-proofed here.
  error?: JobError | null;
  progress?: JobProgressApi;
  estimated_cost?: number | null;
  estimated_duration_seconds?: number | null;
};

function mapEvent(event: WsJobStatusEvent): JobStatusResult {
  return {
    jobId: event.task_id,
    status: normalizeStatus(event.status),
    detail: event.detail ?? undefined,
    videoUrl: event.download_url ? resolveApiUrl(event.download_url) : undefined,
    thumbnailUrl: event.thumbnail_url ? resolveApiUrl(event.thumbnail_url) : undefined,
    title: event.title ?? undefined,
    error: event.error ?? undefined,
    progress: mapJobProgress(event.progress ?? null),
    estimatedCost: event.estimated_cost ?? undefined,
    estimatedDurationSeconds: event.estimated_duration_seconds ?? undefined,
  };
}

const MAX_RETRIES = 4;

/**
 * Opens a WebSocket for a single job's real-time status stream.
 *
 * Returns a close function — call it to tear down the connection cleanly.
 * `onUpdate` is called for every status message the server pushes (including
 * the initial snapshot the backend sends immediately after accepting).
 * `onFallback` is called when the socket gives up after MAX_RETRIES (or on
 * auth failure) — the caller should fall back to REST polling at that point.
 */
export function openVideoSocket(
  jobId: string,
  onUpdate: (result: JobStatusResult) => void,
  onFallback: () => void,
): () => void {
  if (LIBRARY_BACKEND !== 'videos') {
    // WebSocket endpoint only exists on the 'videos' backend.
    queueMicrotask(onFallback);
    return () => { };
  }

  let closed = false;
  let ws: WebSocket | null = null;
  let retries = 0;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;

  async function connect(): Promise<void> {
    if (closed) return;

    let token = getAccessToken();
    if (!token) {
      // A thrown refresh (network failure) and a rejected one both mean no
      // socket right now — the REST fallback is the right response to either.
      token = await performTokenRefresh().catch(() => null);
    }
    /* Two different outcomes that used to share one branch. No token means no
       socket, and REST is the right answer. `closed` means the caller tore this
       down while the refresh was in flight — a job that finished during the
       await — and there is nobody left to poll for. Firing the fallback there
       re-armed the 15 s poller against a finished job, and ProcessingProvider's
       onFallback has no cancelled guard to catch it (ContentPage's does), so
       nothing pruned it until the socket effect happened to re-run. */
    if (closed) return;
    if (!token) {
      onFallback();
      return;
    }

    /* The token rides in the subprotocol list, not the URL — see
       wsAuthProtocols. The server echoes back the marker protocol; if it echoes
       nothing (an older engine that does not know the scheme) the browser fails
       the connection, onclose fires, and the usual retry/REST-fallback path
       handles it, which is the correct outcome for a version mismatch. */
    const url = buildWsUrl(wsStatusPath(jobId));
    ws = new WebSocket(url, wsAuthProtocols(token));

    /* A connection that opens has paid off its predecessor's failure, so the
       budget starts again. Counting for the lifetime of the job instead meant a
       socket behind a proxy that recycles idle connections spent its four
       retries over twenty minutes of perfectly healthy streaming, then dropped
       to REST polling on a drop that would have reconnected like the four
       before it. MAX_RETRIES is meant to give up on a socket that cannot
       connect, not on one that keeps having to. */
    ws.onopen = () => {
      retries = 0;
    };

    ws.onmessage = (ev: MessageEvent) => {
      try {
        const event = JSON.parse(ev.data as string) as WsJobStatusEvent;
        onUpdate(mapEvent(event));
      } catch {
        // Ignore malformed frames.
      }
    };

    ws.onerror = () => {
      // onerror always precedes onclose; handled in onclose.
    };

    ws.onclose = (ev: CloseEvent) => {
      if (closed) return;
      // 1008 = POLICY_VIOLATION: the backend rejected the token or the job
      // doesn't belong to this user — no point retrying.
      if (ev.code === 1008 || retries >= MAX_RETRIES) {
        onFallback();
        return;
      }
      // Exponential back-off: 1 s, 2 s, 4 s, 8 s.
      const delay = 1000 * Math.pow(2, retries);
      retries += 1;
      retryTimeout = setTimeout(() => {
        connect().catch(() => {
          onFallback();
        });
      }, delay);
    };
  }

  connect().catch(() => {
    onFallback();
  });

  return () => {
    closed = true;
    if (retryTimeout !== null) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    if (ws) {
      // Clear onclose before closing so the retry handler doesn't fire on an
      // intentional teardown.
      ws.onclose = null;
      ws.close();
      ws = null;
    }
  };
}
